/**
 * köbi Button Wave-Fill Animation
 * ─────────────────────────────────────────────────────────────
 * Wraps button text content in .koebi-btn__label (position:relative; z-index:1)
 * so it stays above the ::before wave overlay.
 * Manages .is-wave-filling class to enable animation-play-state during entry.
 */
(function () {
  'use strict';

  var SELECTOR = '.button:not(input), .shopify-challenge__button, .customer button, button.shopify-payment-button__button, .koebi-hero__btn';
  var LABEL_CLASS = 'koebi-btn__label';
  var FILLING_CLASS = 'is-wave-filling';
  var FILL_DURATION = 650; // ms – matches CSS transform 0.65s

  // Per-button fill timers stored safely
  var fillTimers = new WeakMap();

  /**
   * Returns true for nodes that should NOT be wrapped
   * (SVG icons, hidden elements, loading overlays, already-wrapped spans)
   */
  function isSkippable(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    var tag = node.tagName.toLowerCase();
    if (tag === 'svg') return true;
    if (node.querySelector && node.querySelector('svg')) return true;
    if (node.getAttribute('aria-hidden') === 'true') return true;
    var cl = node.classList;
    if (
      cl.contains('loading-overlay') ||
      cl.contains('visually-hidden') ||
      cl.contains('icon') ||
      cl.contains('spinner') ||
      cl.contains(LABEL_CLASS)
    ) return true;
    return false;
  }

  /**
   * Wraps the button's visible text content in a .koebi-btn__label span
   * so CSS can apply position:relative; z-index:1 above the wave ::before.
   *
   * Strategy:
   *  1. If there are direct non-empty text nodes → gather them (and any
   *     non-skippable sibling elements in between) into one wrapper span.
   *  2. If there are no direct text nodes but exactly one non-skippable
   *     child element → add LABEL_CLASS directly to that element.
   *  3. Otherwise leave as-is (already wrapped or too complex to touch).
   */
  function wrapButton(btn) {
    if (btn.tagName === 'INPUT' || btn.dataset.waveWrapped) return;
    btn.dataset.waveWrapped = '1';

    var children = Array.from(btn.childNodes);

    // Collect content nodes (non-empty text + non-skippable elements)
    var contentNodes = children.filter(function (n) {
      if (n.nodeType === Node.TEXT_NODE) return n.textContent.trim().length > 0;
      if (n.nodeType === Node.ELEMENT_NODE) return !isSkippable(n);
      return false;
    });

    if (contentNodes.length === 0) return;

    // Case: single non-skippable element child → add class to it
    if (
      contentNodes.length === 1 &&
      contentNodes[0].nodeType === Node.ELEMENT_NODE
    ) {
      contentNodes[0].classList.add(LABEL_CLASS);
      return;
    }

    // Case: one or more text/element nodes → wrap together
    var span = document.createElement('span');
    span.className = LABEL_CLASS;
    btn.insertBefore(span, contentNodes[0]);
    contentNodes.forEach(function (n) { span.appendChild(n); });
  }

  function bindWaveEvents(btn) {
    if (btn.tagName === 'INPUT' || btn.dataset.waveBound) return;
    btn.dataset.waveBound = '1';

    btn.addEventListener('mouseenter', function () {
      var t = fillTimers.get(btn);
      if (t) clearTimeout(t);
      btn.classList.add(FILLING_CLASS);
      fillTimers.set(btn, setTimeout(function () {
        btn.classList.remove(FILLING_CLASS);
        fillTimers.delete(btn);
      }, FILL_DURATION));
    });

    btn.addEventListener('mouseleave', function () {
      var t = fillTimers.get(btn);
      if (t) { clearTimeout(t); fillTimers.delete(btn); }
      btn.classList.remove(FILLING_CLASS);
    });
  }

  function processBtn(btn) {
    wrapButton(btn);
    bindWaveEvents(btn);
  }

  function processAll() {
    document.querySelectorAll(SELECTOR).forEach(processBtn);
  }

  // Initial run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAll);
  } else {
    processAll();
  }

  /**
   * True wenn der Button (als Element) direkte Textknoten hat, die NICHT in
   * .koebi-btn__label gewrapt sind. Das passiert z. B. wenn cart-notification.js
   * `innerHTML = ...` auf den Button schreibt und dabei unsere Wrap-Span killt.
   */
  function needsRewrap(btn) {
    var children = btn.childNodes;
    for (var i = 0; i < children.length; i++) {
      var n = children[i];
      if (n.nodeType === Node.TEXT_NODE && n.textContent.trim().length > 0) {
        return true;
      }
    }
    return false;
  }

  function rewrapIfNeeded(btn) {
    if (needsRewrap(btn)) {
      // Wrap-Marker zurücksetzen, damit wrapButton nicht short-circuited
      delete btn.dataset.waveWrapped;
      wrapButton(btn);
    }
  }

  // Watch for dynamically added buttons (Cart Drawer, Quick Add, etc.) AND
  // for content changes INSIDE existing buttons (cart-notification innerHTML-Update)
  function startObserver() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mut = mutations[i];

        // 1) Neu hinzugefügte Buttons (oder Knoten, die Buttons enthalten)
        var added = mut.addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches && node.matches(SELECTOR)) processBtn(node);
          if (node.querySelectorAll) {
            node.querySelectorAll(SELECTOR).forEach(processBtn);
          }
        }

        // 2) Content-Änderung innerhalb eines bestehenden Buttons
        //    (z. B. cart-notification.js ersetzt innerHTML → Wrap-Span weg)
        var target = mut.target;
        if (
          target &&
          target.nodeType === Node.ELEMENT_NODE &&
          target.matches &&
          target.matches(SELECTOR)
        ) {
          rewrapIfNeeded(target);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startObserver);
  }
})();
